import React, { useEffect, useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
} from 'react-intl';
import { connect } from 'react-redux';
import { IAccountBillTo } from '../../../models/account';
import { RootState } from '../../../store';
import { IAccount } from '../../../models/company-account';
import { ISession } from '../../../models/pro-sites';
import { IPromoDetail } from '../../../models/talend';
import * as accountActions from '../../../store/account/account-actions';
import {
    loadPromoDetails,
    releasePromo,
} from '../../../store/promo-detail/promo-detail-actions';
import PageTitle from '../../common/page.title';
import SelectInput from '../../inputs/select-input';
import AutoShip from './auto-ship';
import Certificate from './certificate';
import ConfirmationModal from './confirmation-modal';

interface IManagePromosProps {
    session: ISession | null;
    billToAccounts: IAccountBillTo[];
    certificates: IPromoDetail[];
    autoShips: IPromoDetail[];
    loadBillToAccounts: () => Promise<void>;
    loadPromoDetails: (billTo: string, shipTo: string) => Promise<void>;
    releasePromo: (
        billTo: string,
        shipTo: string,
        orderNumber: number
    ) => Promise<void>;
}

export const ManagePromotions = ({
    session,
    billToAccounts,
    certificates,
    autoShips,
    loadBillToAccounts,
    loadPromoDetails,
    releasePromo,
}: IManagePromosProps) => {
    const initialBillTo = {
        fullAccountNumber: '',
        shipToAccounts: Array<IAccount>(),
    } as IAccountBillTo;

    const [selectedBillTo, setBillTo] = useState<IAccountBillTo>(initialBillTo);
    const [selectedShipTo, setShipTo] = useState<string>('');
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (billToAccounts.length === 0) {
            loadBillToAccounts();
        }

        if (
            selectedBillTo.fullAccountNumber.length !== 0 &&
            selectedShipTo.length !== 0
        ) {
            loadPromoDetails(selectedBillTo.fullAccountNumber, selectedShipTo);
        }
    }, [selectedBillTo, selectedShipTo]);

    const handleChange = (event: any) => {
        const { name, value } = event.target;

        if (name === 'billTo' && value !== '') {
            const billTo = billToAccounts.find(
                (bt) => bt.fullAccountNumber === value
            ) as IAccountBillTo;
            setBillTo(billTo);
        }

        if (name === 'shipTo' && value !== '') {
            setShipTo(value);
        }
    };

    const onPromoDetailRelease = async (event: any, orderNumber: number) => {
        await releasePromo(
            selectedBillTo.fullAccountNumber,
            selectedShipTo,
            orderNumber
        );
        setShowModal(true);
    };

    const handleModalAccept = async () => {
        setShowModal(false);
    };

    return (
        <>
            {session && (
                <>
                    <PageTitle pageTitleId="Manage_Promotions" />
                    <div className="row">
                        <div className="col-sm-12">
                            <h1>
                                <FormattedMessage id="Manage_Promotions" />
                            </h1>
                        </div>
                    </div>

                    <div className="jumbotron">
                        <div className="row">
                            <div className="col-md-12">
                                <SelectInput
                                    name="billTo"
                                    label={'Bill To Account'}
                                    options={billToAccounts.map((billTo) => ({
                                        value: billTo.fullAccountNumber,
                                        text: billTo.fullAccountNumber,
                                    }))}
                                    hasDefaultOption={true}
                                    defaultOption={'Select'}
                                    onChange={handleChange}
                                    value={selectedBillTo.fullAccountNumber}
                                    required={true}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12">&nbsp;</div>
                        </div>
                        <div className="row">
                            <div className="col-md-12">
                                <SelectInput
                                    name="shipTo"
                                    label={'Ship To Account'}
                                    options={selectedBillTo.shipToAccounts.map(
                                        (shipTo) => ({
                                            value: shipTo.fullAccountNumber,
                                            text: shipTo.fullAccountNumber,
                                        })
                                    )}
                                    hasDefaultOption={true}
                                    defaultOption={'Select'}
                                    onChange={handleChange}
                                    value={selectedShipTo}
                                    required={true}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="jumbotron">
                        <div className="row">
                            <div className="col-md-12">
                                <h2>
                                    <FormattedMessage id="Certificates" />
                                </h2>
                                {certificates.length === 0 && (
                                    <h3>
                                        <FormattedMessage id="No_certificates_found" />
                                    </h3>
                                )}
                            </div>
                        </div>

                        {certificates.map((cert) => {
                            return (
                                <div className="row" key={cert.promotionName}>
                                    <div className="col-sm-12">
                                        <Certificate
                                            promoDetail={cert}
                                            onRelease={onPromoDetailRelease}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="jumbotron">
                        <div className="row">
                            <div className="col-md-12">
                                <h2>
                                    <FormattedMessage id="Autoships" />
                                </h2>
                                {autoShips.length === 0 && (
                                    <h3>
                                        <FormattedMessage id="No_autoships_found" />
                                    </h3>
                                )}
                            </div>
                        </div>

                        {autoShips.map((autoShip) => {
                            return (
                                <div
                                    className="row"
                                    key={autoShip.promotionName}
                                >
                                    <div className="col-sm-12">
                                        <AutoShip
                                            promoDetail={autoShip}
                                            onRelease={onPromoDetailRelease}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            {showModal && (
                <ConfirmationModal
                    onAccept={handleModalAccept}
                    onClose={() => void 0}
                />
            )}
        </>
    );
};

const mapStateToProps = (state: RootState) => {
    const promoDetails =
        state.promoDetails && state.promoDetails.data
            ? state.promoDetails.data!
            : [];

    return {
        session: state.session ? state.session.data! : null,
        constants: state.constants,
        billToAccounts: state.billToAccounts ? state.billToAccounts.data! : [],
        certificates: promoDetails.filter(
            (pd) => pd.promotionType.toLocaleLowerCase() === 'cert'
        ),
        autoShips: promoDetails.filter(
            (pd) => pd.promotionType.toLocaleLowerCase() === 'create'
        ),
        loading: state.apiCallsInProgress > 0,
    };
};

const mapDispatchToProps = {
    loadBillToAccounts: accountActions.loadBillToAccounts,
    loadPromoDetails,
    releasePromo,
};

const managePromotionsContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(ManagePromotions);

export default managePromotionsContainer;
